import React, { ReactNode } from 'react';
import { isEmpty } from 'lodash';
import { TemplatesDataContext } from '@fiverr-private/legal_documents_package';
import { ComplianceState } from '../../types/complianceTypes';
import usePaymentSuccessToast from '../../hooks/usePaymentSuccessToast';
import { useComplianceActionModal } from '../../hooks/useComplianceAcitonModal';
import { useOrganizationConfigData } from '../../api/useOrganizationConfigData';
import { backgroundCheckStatusRank, getLegalDocumentsRank, workforceClassificationRank } from './utils';
import { SellerComplianceStateType } from './types';
import { ComplianceContext, Context } from './context';

export interface ComplianceProviderProps {
    children: ReactNode;
    organizationId: string | undefined;
    requester_id: number | undefined;
    complianceData?: ComplianceState;
    pageName: string;
}

export const ComplianceProviderContent = ({
    children,
    organizationId,
    requester_id,
    complianceData: serverComplianceData,
    pageName = '',
}: ComplianceProviderProps) => {
    usePaymentSuccessToast();
    const { data: complianceData } = useOrganizationConfigData(serverComplianceData);
    const { ModalContent, ...modalActionHandlers } = useComplianceActionModal({ pageName });

    const value: Context = {
        complianceData,
        pageName,
        ...modalActionHandlers,
        getSellerComplianceState(sellerId: number): SellerComplianceStateType {
            const backgroundChecks = complianceData?.backgroundCheckData?.[sellerId] ?? [];
            const backgroundCheckData = backgroundChecks[backgroundChecks.length - 1];
            const isSellerHasBackgroundCheckProcess = !isEmpty(backgroundCheckData) ?? false;

            const classificationRequests = complianceData?.workforceClassificationData?.[sellerId] ?? [];
            const classificationRequestData = classificationRequests[classificationRequests.length - 1];
            const isSellerHasClassificationProcess = !isEmpty(classificationRequestData) ?? false;

            const legalDocsData = complianceData?.legalDocsData;
            const isSellerHasSentLegalDocs = !isEmpty(legalDocsData?.statsBySeller?.[sellerId]?.statuses);

            const hasBackgroundCheckProcess = !isEmpty(complianceData?.backgroundCheckData);
            const hasClassificationRequestProcess = !isEmpty(complianceData?.workforceClassificationData);
            const hasLegalDocumentsProcess = !!legalDocsData?.totalTemplates;
            const hasComplianceProcess =
                hasBackgroundCheckProcess || hasClassificationRequestProcess || hasLegalDocumentsProcess;
            const isSellerHasComplianceProcess =
                isSellerHasBackgroundCheckProcess || isSellerHasClassificationProcess || isSellerHasSentLegalDocs;

            const backgroundCheckRank = backgroundCheckStatusRank(backgroundCheckData);

            const classificationRank = workforceClassificationRank(classificationRequestData);
            const legalDocsRank = getLegalDocumentsRank(legalDocsData?.statsBySeller?.[sellerId]);
            const compliaceRank = Math.max(backgroundCheckRank, classificationRank, legalDocsRank);

            const hasLegalDocsSetup = !!legalDocsData?.totalTemplates;

            const isBackgroundCheckRequestorYou = requester_id === backgroundCheckData?.requesterId;
            const isClassificationRequestorYou = requester_id === classificationRequestData?.requesterId;

            return {
                backgroundCheckData,
                hasBackgroundCheckProcess,
                hasComplianceProcess,
                compliaceRank,
                backgroundCheckRank,
                hasLegalDocsSetup,
                isSellerHasBackgroundCheckProcess,
                isSellerHasClassificationProcess,
                classificationRequestData,
                classificationRank,
                isBackgroundCheckRequestorYou,
                isClassificationRequestorYou,
                isSellerHasComplianceProcess,
                legalDocsInitialData: complianceData?.legalDocsData?.statsBySeller?.[sellerId],
                legalDocsData,
            };
        },
    };

    if (!organizationId || !requester_id) {
        return <>{children}</>;
    }

    return (
        <ComplianceContext.Provider value={value}>
            <>
                {children}
                {ModalContent}
            </>
        </ComplianceContext.Provider>
    );
};

export const ComplianceProvider = ({ children, ...props }: ComplianceProviderProps) => (
    <TemplatesDataContext withTemplatesUsage={false}>
        <ComplianceProviderContent {...props}>{children}</ComplianceProviderContent>
    </TemplatesDataContext>
);
